import React, { useState, useEffect } from 'react';
import { CurrencyEuroIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { GasInvoice, GasInvoiceLine, GasInvoiceStatus, GasContract, GasPod, GasMeterReading, CreateGasInvoiceData } from '../../types/gas';
import { gasInvoiceService, gasContractService, gasPodService, gasMeterReadingService } from '../../services/gasService';
import { useLanguage } from '../../contexts/LanguageContext';
import toast from 'react-hot-toast';

interface GasInvoicesSectionProps {
  customerId: string;
}

const GasInvoicesSection: React.FC<GasInvoicesSectionProps> = ({ customerId }) => {
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState<GasInvoice[]>([]);
  const [contracts, setContracts] = useState<GasContract[]>([]);
  const [gasPods, setGasPods] = useState<GasPod[]>([]);
  const [readings, setReadings] = useState<GasMeterReading[]>([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState<Partial<CreateGasInvoiceData>>({
    billingPeriodStart: '',
    billingPeriodEnd: '',
    gasDays: 30,
    currency: 'HUF',
    status: 'draft',
    lines: [],
    notes: ''
  });

  const loadData = async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const [invoiceData, contractData, podData, readingData] = await Promise.all([
        gasInvoiceService.getGasInvoicesByCustomerId(customerId),
        gasContractService.getGasContractsByCustomerId(customerId),
        gasPodService.getGasPodsByCustomerId(customerId),
        gasMeterReadingService.getGasMeterReadingsByCustomerId(customerId)
      ]);
      setInvoices(invoiceData);
      setContracts(contractData);
      setGasPods(podData);
      setReadings(readingData);
    } catch (error) {
      console.error('Error loading gas invoices:', error);
      toast.error(t('gas.messages.loadError', { name: t('gas.sections.invoices') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [customerId]);

  const resetForm = () => {
    setFormData({
      billingPeriodStart: '',
      billingPeriodEnd: '',
      gasDays: 30,
      currency: 'HUF',
      status: 'draft',
      lines: [],
      notes: ''
    });
  };

  const calculateTotals = (lines: GasInvoiceLine[]) => {
    const totalNet = lines.reduce((sum, line) => sum + line.netAmount, 0);
    const totalVat = lines.reduce((sum, line) => sum + line.vatAmount, 0);
    const totalGross = lines.reduce((sum, line) => sum + line.grossAmount, 0);
    return { totalNet, totalVat, totalGross };
  };

  const calculateGasDays = (start: string, end: string): number => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffTime = endDate.getTime() - startDate.getTime();
    return Math.max(1, Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1);
  };

  const handleAutoFill = () => {
    const contract = contracts.find(c => c.id === formData.contractId);
    if (!contract) {
      toast.error(t('gas.invoice.errors.contractRequired'));
      return;
    }
    if (!formData.billingPeriodStart || !formData.billingPeriodEnd) {
      toast.error(t('gas.invoice.errors.periodRequired'));
      return;
    }

    const start = formData.billingPeriodStart;
    const end = formData.billingPeriodEnd;
    const podIds = contract.gasPodIds && contract.gasPodIds.length > 0
      ? contract.gasPodIds
      : gasPods.map(p => p.id);

    const periodReadings = readings.filter(r => {
      const day = r.gasDay;
      return podIds.includes(r.gasPodId) && day >= start && day <= end;
    });

    const totalEnergyKwh = periodReadings.reduce((sum, r) => sum + (r.energyKwh || 0), 0);
    const totalConsumptionM3 = periodReadings.reduce((sum, r) => sum + (r.consumptionM3 || 0), 0);
    const gasDays = calculateGasDays(start, end);

    const lines: GasInvoiceLine[] = [];
    const vatRate = contract.vatRate || 27;

    if (contract.contractType === 'energy' && totalEnergyKwh > 0 && contract.price) {
      const netAmount = Number((totalEnergyKwh * contract.price).toFixed(2));
      const vatAmount = Number((netAmount * (vatRate / 100)).toFixed(2));
      lines.push({
        description: `Energia díj (${totalEnergyKwh.toLocaleString('hu-HU')} kWh)`,
        quantity: Number(totalEnergyKwh.toFixed(2)),
        unit: contract.priceUnit?.includes('MWh') ? 'MWh' : 'kWh',
        unitPrice: contract.priceUnit?.includes('MWh') ? Number((contract.price / 1000).toFixed(6)) : contract.price,
        netAmount,
        vatRate,
        vatAmount,
        grossAmount: Number((netAmount + vatAmount).toFixed(2)),
        type: 'energy'
      });
    }

    if (contract.contractType === 'rhd_variable' && totalEnergyKwh > 0 && contract.price) {
      const netAmount = Number((totalEnergyKwh * contract.price).toFixed(2));
      const vatAmount = Number((netAmount * (vatRate / 100)).toFixed(2));
      lines.push({
        description: `RHD forgalom-arányos díj (${totalEnergyKwh.toLocaleString('hu-HU')} kWh)`,
        quantity: Number(totalEnergyKwh.toFixed(2)),
        unit: contract.priceUnit?.includes('MWh') ? 'MWh' : 'kWh',
        unitPrice: contract.priceUnit?.includes('MWh') ? Number((contract.price / 1000).toFixed(6)) : contract.price,
        netAmount,
        vatRate,
        vatAmount,
        grossAmount: Number((netAmount + vatAmount).toFixed(2)),
        type: 'rhd'
      });
    }

    if (contract.contractType === 'rhd_fixed' && contract.capacityKwh && contract.price) {
      const netAmount = Number((contract.capacityKwh * contract.price).toFixed(2));
      const vatAmount = Number((netAmount * (vatRate / 100)).toFixed(2));
      lines.push({
        description: `RHD fix / teljesítmény díj (${gasDays} gáznap)`,
        quantity: contract.capacityKwh,
        unit: 'kWh',
        unitPrice: contract.price,
        netAmount,
        vatRate,
        vatAmount,
        grossAmount: Number((netAmount + vatAmount).toFixed(2)),
        type: 'rhd'
      });
    }

    if (totalConsumptionM3 > 0 && !lines.find(l => l.type === 'energy')) {
      const netAmount = Number((totalConsumptionM3 * (contract.price || 0)).toFixed(2));
      if (netAmount > 0) {
        const vatAmount = Number((netAmount * (vatRate / 100)).toFixed(2));
        lines.push({
          description: `Földgáz fogyasztás (${totalConsumptionM3.toLocaleString('hu-HU')} m³)`,
          quantity: Number(totalConsumptionM3.toFixed(2)),
          unit: 'm³',
          unitPrice: contract.price || 0,
          netAmount,
          vatRate,
          vatAmount,
          grossAmount: Number((netAmount + vatAmount).toFixed(2)),
          type: 'energy'
        });
      }
    }

    if (lines.length === 0) {
      toast.error(t('gas.invoice.autoFillError'));
      return;
    }

    setFormData({
      ...formData,
      gasDays,
      currency: contract.currency || 'HUF',
      lines,
      notes: `Automatikusan generált a(z) ${contract.contractNumber || contract.contractType} szerződés alapján.`
    });
    toast.success(`${lines.length} ${t('gas.invoice.autoFillSuccess')}`);
  };

  const addLine = () => {
    const newLine: GasInvoiceLine = {
      description: 'Új tétel',
      quantity: 1,
      unit: 'db',
      unitPrice: 0,
      netAmount: 0,
      vatRate: 27,
      vatAmount: 0,
      grossAmount: 0,
      type: 'energy'
    };
    setFormData({ ...formData, lines: [...(formData.lines || []), newLine] });
  };

  const updateLine = (index: number, field: keyof GasInvoiceLine, value: any) => {
    const lines = [...(formData.lines || [])];
    const line = { ...lines[index], [field]: value };

    if (field === 'quantity' || field === 'unitPrice' || field === 'vatRate') {
      line.netAmount = Number((line.quantity * line.unitPrice).toFixed(2));
      line.vatAmount = Number((line.netAmount * (line.vatRate / 100)).toFixed(2));
      line.grossAmount = Number((line.netAmount + line.vatAmount).toFixed(2));
    }

    lines[index] = line;
    setFormData({ ...formData, lines });
  };

  const removeLine = (index: number) => {
    const lines = [...(formData.lines || [])];
    lines.splice(index, 1);
    setFormData({ ...formData, lines });
  };

  const handleSave = async () => {
    if (!formData.billingPeriodStart || !formData.billingPeriodEnd) {
      toast.error(t('gas.invoice.errors.periodRequired'));
      return;
    }
    if (!formData.lines || formData.lines.length === 0) {
      toast.error(t('gas.invoice.errors.noLines'));
      return;
    }

    const { totalNet, totalVat, totalGross } = calculateTotals(formData.lines || []);

    try {
      await gasInvoiceService.createGasInvoice({
        customerId,
        billingPeriodStart: formData.billingPeriodStart,
        billingPeriodEnd: formData.billingPeriodEnd,
        gasDays: formData.gasDays || 30,
        status: formData.status as GasInvoiceStatus || 'draft',
        lines: formData.lines || [],
        totalNet,
        totalVat,
        totalGross,
        currency: formData.currency || 'HUF',
        notes: formData.notes?.trim() || undefined
      });
      toast.success(t('gas.messages.addSuccess', { name: t('gas.sections.invoices') }));
      setShowAdd(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating gas invoice:', error);
      toast.error(t('gas.messages.addError', { name: t('gas.sections.invoices') }));
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('gas.messages.confirmDelete'))) return;
    try {
      await gasInvoiceService.deleteGasInvoice(id);
      toast.success(t('gas.messages.deleteSuccess', { name: t('gas.sections.invoices') }));
      loadData();
    } catch (error) {
      console.error('Error deleting gas invoice:', error);
      toast.error(t('gas.messages.deleteError', { name: t('gas.sections.invoices') }));
    }
  };

  const getStatusLabel = (status: GasInvoiceStatus) => {
    switch (status) {
      case 'draft': return 'Vázlat';
      case 'issued': return 'Kiállítva';
      case 'paid': return 'Fizetve';
      case 'cancelled': return 'Sztornó';
      default: return status;
    }
  };

  const { totalNet, totalVat, totalGross } = calculateTotals(formData.lines || []);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CurrencyEuroIcon className="h-6 w-6 text-emerald-600" />
          <h2 className="text-lg font-semibold text-gray-900">{t('gas.sections.invoices')}</h2>
        </div>
        <button
          type="button"
          onClick={() => setShowAdd(!showAdd)}
          className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 text-sm font-medium transition-colors"
        >
          <PlusIcon className="h-4 w-4" />
          {showAdd ? t('gas.common.cancel') : t('gas.common.new')}
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">{t('gas.common.loading')}</p>
      ) : invoices.length === 0 ? (
        <p className="text-sm text-gray-500">{t('gas.common.noData')}</p>
      ) : (
        <div className="space-y-2">
          {invoices.map(invoice => (
            <div key={invoice.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm font-semibold text-gray-900">{invoice.invoiceNumber || '—'}</span>
                  <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full">{getStatusLabel(invoice.status)}</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {invoice.billingPeriodStart} → {invoice.billingPeriodEnd} ({invoice.gasDays} gáznap)
                </p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Nettó: {invoice.totalNet.toLocaleString('hu-HU')} {invoice.currency} | 
                  ÁFA: {invoice.totalVat.toLocaleString('hu-HU')} | 
                  Bruttó: {invoice.totalGross.toLocaleString('hu-HU')} {invoice.currency}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => handleDelete(invoice.id)}
                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200 space-y-3">
          <h3 className="text-sm font-semibold text-emerald-900 mb-2">{t('gas.sections.invoices')}</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.invoice.fields.contract')}</label>
              <select
                value={formData.contractId || ''}
                onChange={e => setFormData({ ...formData, contractId: e.target.value || undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="">{t('gas.invoice.fields.placeholder.selectContract')}</option>
                {contracts.map(contract => (
                  <option key={contract.id} value={contract.id}>
                    {contract.contractNumber || contract.contractType} ({contract.startDate}{contract.endDate ? ` - ${contract.endDate}` : ''})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                type="button"
                onClick={handleAutoFill}
                disabled={!formData.contractId || !formData.billingPeriodStart || !formData.billingPeriodEnd}
                className="w-full px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {t('gas.invoice.fields.autoFill')}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.invoice.fields.billingPeriodStart')} *</label>
              <input
                type="date"
                value={formData.billingPeriodStart || ''}
                onChange={e => {
                  const start = e.target.value;
                  const end = formData.billingPeriodEnd || '';
                  setFormData({
                    ...formData,
                    billingPeriodStart: start,
                    gasDays: start && end ? calculateGasDays(start, end) : formData.gasDays
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.invoice.fields.billingPeriodEnd')} *</label>
              <input
                type="date"
                value={formData.billingPeriodEnd || ''}
                onChange={e => {
                  const end = e.target.value;
                  const start = formData.billingPeriodStart || '';
                  setFormData({
                    ...formData,
                    billingPeriodEnd: end,
                    gasDays: start && end ? calculateGasDays(start, end) : formData.gasDays
                  });
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.invoice.fields.gasDays')}</label>
              <input
                type="number"
                value={formData.gasDays || ''}
                onChange={e => setFormData({ ...formData, gasDays: e.target.value ? Number(e.target.value) : 30 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.invoice.fields.currency')}</label>
              <select
                value={formData.currency || 'HUF'}
                onChange={e => setFormData({ ...formData, currency: e.target.value as 'HUF' | 'EUR' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="HUF">HUF</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.invoice.fields.status')}</label>
              <select
                value={formData.status || 'draft'}
                onChange={e => setFormData({ ...formData, status: e.target.value as GasInvoiceStatus })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              >
                <option value="draft">{t('gas.invoice.statuses.draft')}</option>
                <option value="issued">{t('gas.invoice.statuses.issued')}</option>
                <option value="paid">{t('gas.invoice.statuses.paid')}</option>
                <option value="cancelled">{t('gas.invoice.statuses.cancelled')}</option>
              </select>
            </div>
          </div>

          {/* Számlasorok */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="block text-xs font-medium text-gray-700">{t('gas.invoice.fields.lines')}</label>
              <button
                type="button"
                onClick={addLine}
                className="flex items-center gap-1 px-2 py-1 bg-emerald-100 text-emerald-700 rounded text-xs font-medium hover:bg-emerald-200"
              >
                <PlusIcon className="h-3 w-3" /> {t('gas.invoice.fields.addLine')}
              </button>
            </div>

            {formData.lines && formData.lines.length > 0 ? (
              <div className="space-y-2">
                {formData.lines.map((line, index) => (
                  <div key={index} className="grid grid-cols-12 gap-2 p-2 bg-white rounded-lg border border-gray-200 items-end">
                    <div className="col-span-3">
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{t('gas.invoice.fields.lineDescription')}</label>
                      <input
                        type="text"
                        value={line.description}
                        onChange={e => updateLine(index, 'description', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{t('gas.invoice.fields.lineType')}</label>
                      <select
                        value={line.type}
                        onChange={e => updateLine(index, 'type', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      >
                        <option value="energy">{t('gas.invoice.lineTypes.energy')}</option>
                        <option value="rhd">{t('gas.invoice.lineTypes.rhd')}</option>
                        <option value="capacity">{t('gas.invoice.lineTypes.capacity')}</option>
                        <option value="penalty">{t('gas.invoice.lineTypes.penalty')}</option>
                        <option value="fee">{t('gas.invoice.lineTypes.fee')}</option>
                        <option value="other">{t('gas.invoice.lineTypes.other')}</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{t('gas.invoice.fields.lineQuantity')}</label>
                      <input
                        type="number"
                        step="0.01"
                        value={line.quantity}
                        onChange={e => updateLine(index, 'quantity', Number(e.target.value))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{t('gas.invoice.fields.lineUnit')}</label>
                      <input
                        type="text"
                        value={line.unit}
                        onChange={e => updateLine(index, 'unit', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{t('gas.invoice.fields.lineUnitPrice')}</label>
                      <input
                        type="number"
                        step="0.0001"
                        value={line.unitPrice}
                        onChange={e => updateLine(index, 'unitPrice', Number(e.target.value))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div className="col-span-1">
                      <label className="block text-[10px] font-medium text-gray-500 mb-0.5">{t('gas.invoice.fields.lineVatRate')}</label>
                      <input
                        type="number"
                        value={line.vatRate}
                        onChange={e => updateLine(index, 'vatRate', Number(e.target.value))}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                      />
                    </div>
                    <div className="col-span-1 flex justify-end">
                      <button
                        type="button"
                        onClick={() => removeLine(index)}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="col-span-12 text-xs text-gray-600 text-right">
                      {t('gas.invoice.totalNet')}: {line.netAmount.toLocaleString('hu-HU')} | {t('gas.invoice.totalVat')}: {line.vatAmount.toLocaleString('hu-HU')} | {t('gas.invoice.totalGross')}: {line.grossAmount.toLocaleString('hu-HU')}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-500 italic">{t('gas.invoice.noLines')}</p>
            )}
          </div>

          <div className="bg-white p-3 rounded-lg border border-gray-200">
            <div className="flex justify-between text-sm font-medium text-gray-900">
              <span>{t('gas.invoice.totalNet')}:</span>
              <span>{totalNet.toLocaleString('hu-HU')} {formData.currency}</span>
            </div>
            <div className="flex justify-between text-sm text-gray-600">
              <span>{t('gas.invoice.totalVat')}:</span>
              <span>{totalVat.toLocaleString('hu-HU')} {formData.currency}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold text-emerald-700 mt-1 pt-1 border-t border-gray-200">
              <span>{t('gas.invoice.totalGross')}:</span>
              <span>{totalGross.toLocaleString('hu-HU')} {formData.currency}</span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">{t('gas.invoice.fields.notes')}</label>
            <textarea
              value={formData.notes || ''}
              onChange={e => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => {
                setShowAdd(false);
                resetForm();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
            >
              {t('gas.common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-700"
            >
              {t('gas.common.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GasInvoicesSection;
